import React, { useMemo, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../store'
import { User, StartingPointsEntry } from '../types/room'
import { DetailedPrediction } from '../types/detailedPrediction'
import { formatPredictionScore } from '../utils/predictionHiding'
import {
  isMatchPlayedWithScore,
  isMatchUpcomingForPlayer,
  sortPlayedByKickoffDesc,
  sortUpcomingByKickoff,
} from '../utils/playerPredictionFilters'
import { getMatchDate, formatDate } from '../utils/dateHelper'
import { formatEarnedPossible, formatPoints } from '../utils/points-label'
import MatchTeamsInline from './MatchTeamsInline'
import { resolveMatchLeague } from '../constants/worldCup2026TeamFlags'
import { useTranslation } from '../hooks/use-i18n'
import futureChampionService from '../services/futureChampionService'
import topScorerService from '../services/topScorerService'
import matchService from '../services/matchService'
import roomsService from '../services/roomsService'
import { FutureChampionEvent, FutureChampionPrediction } from '../types/futureChampion'
import { TopScorerEvent, TopScorerPrediction } from '../types/topScorer'
import {
  PlayerPredictionStats,
  PointsBreakdownBucket,
  StatsPeriod,
} from '../types/playerPredictionStats'
import {
  BarChart3,
  CheckCircle2,
  Clock,
  Coins,
  Flag,
  Gamepad2,
  Medal,
  Scale,
  Target,
  Trophy,
  type LucideIcon,
} from 'lucide-react'

export interface DemoPlayerBundle {
  stats?: PlayerPredictionStats | null
  futureChampionPredictions?: FutureChampionPrediction[]
  topScorerPredictions?: TopScorerPrediction[]
  /** Admin starting-points ledger to show on the Played tab (demo only). */
  startingEntries?: StartingPointsEntry[]
  startingTotal?: number
}

type StatAccent = 'sky' | 'violet' | 'cyan' | 'amber' | 'slate' | 'orange' | 'emerald' | 'blue'

const STAT_ACCENT: Record<
  StatAccent,
  { border: string; iconBg: string; iconColor: string; badgePartial: string; cardBorder: string }
> = {
  sky: {
    border: 'border-l-sky-400',
    iconBg: 'bg-sky-500/15',
    iconColor: 'text-sky-400',
    badgePartial: 'bg-sky-500/20 text-sky-300 border border-sky-500/30',
    cardBorder: 'border-sky-500/20',
  },
  violet: {
    border: 'border-l-violet-400',
    iconBg: 'bg-violet-500/15',
    iconColor: 'text-violet-400',
    badgePartial: 'bg-violet-500/20 text-violet-300 border border-violet-500/30',
    cardBorder: 'border-violet-500/20',
  },
  cyan: {
    border: 'border-l-cyan-400',
    iconBg: 'bg-cyan-500/15',
    iconColor: 'text-cyan-400',
    badgePartial: 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30',
    cardBorder: 'border-cyan-500/20',
  },
  amber: {
    border: 'border-l-amber-400',
    iconBg: 'bg-amber-500/15',
    iconColor: 'text-amber-400',
    badgePartial: 'bg-amber-500/20 text-amber-300 border border-amber-500/30',
    cardBorder: 'border-amber-500/20',
  },
  slate: {
    border: 'border-l-slate-400',
    iconBg: 'bg-slate-500/20',
    iconColor: 'text-slate-300',
    badgePartial: 'bg-slate-500/20 text-slate-200 border border-slate-400/30',
    cardBorder: 'border-slate-500/20',
  },
  orange: {
    border: 'border-l-orange-400',
    iconBg: 'bg-orange-500/15',
    iconColor: 'text-orange-400',
    badgePartial: 'bg-orange-500/20 text-orange-300 border border-orange-500/30',
    cardBorder: 'border-orange-500/20',
  },
  emerald: {
    border: 'border-l-emerald-400',
    iconBg: 'bg-emerald-500/15',
    iconColor: 'text-emerald-400',
    badgePartial: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30',
    cardBorder: 'border-emerald-500/20',
  },
  blue: {
    border: 'border-l-blue-400',
    iconBg: 'bg-blue-500/15',
    iconColor: 'text-blue-400',
    badgePartial: 'bg-blue-500/20 text-blue-300 border border-blue-500/30',
    cardBorder: 'border-blue-500/20',
  },
}

interface ViewPlayerStatsGamesModalProps {
  player: User
  isOpen: boolean
  onClose: () => void
  /** Which tab to show first when the modal opens (user can switch tabs inside the modal). */
  initialViewType?: 'stats' | 'games' | 'upcoming'
  roomId: string
  gameType?: 'football' | 'formula1' | 'golf'
  isLoading?: boolean
  hasMorePredictions?: boolean
  isLoadingMore?: boolean
  onLoadMore?: () => void
  /** Showcase demo: skip API and use injected data */
  demoPlayerBundle?: DemoPlayerBundle
}

const ViewPlayerStatsGamesModal: React.FC<ViewPlayerStatsGamesModalProps> = ({
  player,
  isOpen,
  onClose,
  initialViewType = 'games',
  roomId,
  gameType,
  isLoading = false,
  hasMorePredictions = false,
  isLoadingMore = false,
  onLoadMore,
  demoPlayerBundle,
}) => {
  const { t } = useTranslation()
  const { user: currentUser } = useSelector((state: RootState) => state.auth)
  const singleRoom = useSelector((state: RootState) => state.rooms.singleRoom)
  const [futureChampionPredictions, setFutureChampionPredictions] = useState<FutureChampionPrediction[]>([])
  const [topScorerPredictions, setTopScorerPredictions] = useState<TopScorerPrediction[]>([])
  const [loadingExtras, setLoadingExtras] = useState(false)
  const [statsPeriod, setStatsPeriod] = useState<StatsPeriod>('30d')
  const [playerStats, setPlayerStats] = useState<PlayerPredictionStats | null>(null)
  const [loadingStats, setLoadingStats] = useState(false)
  // Tab is managed inside the modal so the user can toggle Played / Upcoming / Stats without closing.
  const [viewType, setViewType] = useState<'stats' | 'games' | 'upcoming'>(initialViewType)
  const [startingEntries, setStartingEntries] = useState<StartingPointsEntry[]>([])
  const [startingTotal, setStartingTotal] = useState<number>(player.startingPoints ?? 0)

  const playerId = player._id ?? (player as { id?: string }).id ?? ''

  // Reset to the requested initial tab whenever the modal opens for a (new) player.
  useEffect(() => {
    if (isOpen) setViewType(initialViewType)
  }, [isOpen, initialViewType, playerId])

  // Fetch the admin starting-points ledger (football only) for the Played tab.
  useEffect(() => {
    if (!isOpen || viewType !== 'games' || !roomId || !playerId) return
    if (demoPlayerBundle) {
      setStartingEntries(demoPlayerBundle.startingEntries ?? [])
      setStartingTotal(demoPlayerBundle.startingTotal ?? 0)
      return
    }
    let cancelled = false
    roomsService
      .getPlayerStartingPoints(roomId, playerId)
      .then((res) => {
        if (cancelled) return
        setStartingEntries(res.entries ?? [])
        setStartingTotal(res.total ?? 0)
      })
      .catch(() => {
        if (!cancelled) setStartingEntries([])
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, viewType, roomId, playerId, demoPlayerBundle])

  // For games view: use room gameType or default to 'football' so future champion still shows if room.gameType was omitted (e.g. after deploy)
  const effectiveGameType: 'football' | 'formula1' | undefined =
    viewType === 'games'
      ? (gameType === 'football' || gameType === 'formula1' ? gameType : undefined)
      : (gameType === 'football' || gameType === 'formula1' ? gameType : undefined)

  const eventOrder = (name: string) => {
    if (!name) return 99
    const n = name.toLowerCase()
    if (n.includes('start')) return 1
    if (n.includes('second')) return 2
    if (n.includes('third')) return 3
    return 99
  }

  // Same as mobileBrc: derive events from player predictions (each prediction has embedded event from API)
  const slotsToShow = useMemo(() => {
    const list = (futureChampionPredictions ?? []).filter((p) => p.eventId || (p as any).event)
    const events = list
      .map((p) => {
        const ev = (p as any).event
        const id = ev?.id ?? p.eventId
        return {
          id,
          _id: ev?._id ?? p.eventId,
          name: ev?.name ?? 'Future champion',
          gameType: (ev?.gameType ?? 'football') as 'football' | 'formula1',
          tournamentName: ev?.tournamentName,
          eventDate: ev?.eventDate ?? '',
          choices: ev?.choices ?? [],
          isActive: ev?.isActive ?? false,
          actualChampion: ev?.actualChampion,
          createdAt: ev?.createdAt ?? '',
          updatedAt: ev?.updatedAt ?? '',
        } as FutureChampionEvent
      })
      .sort((a, b) => {
        const da = a.eventDate || ''
        const db = b.eventDate || ''
        if (da && db) return db.localeCompare(da)
        return eventOrder(a.name) - eventOrder(b.name)
      })
    const resolvedFiltered =
      viewType === 'games'
        ? events.filter((e) => !!e.actualChampion)
        : viewType === 'upcoming'
          ? events.filter((e) => !e.actualChampion)
          : events
    const n = effectiveGameType === 'football' ? 2 : 3
    return resolvedFiltered.slice(0, n)
  }, [futureChampionPredictions, effectiveGameType, viewType])

  const predictionByEventId = useMemo(() => {
    const map: Record<string, FutureChampionPrediction> = {}
    futureChampionPredictions.forEach((p) => {
      const eid = p.eventId ?? (p.event as any)?.id
      if (eid) map[eid] = p
    })
    return map
  }, [futureChampionPredictions])

  const topScorerSlotsToShow = useMemo(() => {
    const list = (topScorerPredictions ?? []).filter((p) => p.eventId || (p as any).event)
    const events = list
      .map((p) => {
        const ev = (p as any).event
        const id = ev?.id ?? p.eventId
        return {
          id,
          _id: ev?._id ?? p.eventId,
          name: ev?.name ?? 'Top scorer',
          league: ev?.league,
          gameType: ev?.gameType ?? 'football',
          eventDate: ev?.eventDate ?? '',
          choices: Array.isArray(ev?.choices) ? ev.choices : [],
          isActive: ev?.isActive ?? false,
          actualTopScorer: ev?.actualTopScorer,
        } as TopScorerEvent
      })
      .sort((a, b) => (b.eventDate || '').localeCompare(a.eventDate || ''))
    if (viewType === 'games') return events.filter((e) => !!e.actualTopScorer)
    if (viewType === 'upcoming') return events.filter((e) => !e.actualTopScorer)
    return events
  }, [topScorerPredictions, viewType])

  const topScorerPredictionByEventId = useMemo(() => {
    const map: Record<string, TopScorerPrediction> = {}
    topScorerPredictions.forEach((p) => {
      const eid = p.eventId ?? (p.event as any)?.id
      if (eid) map[eid] = p
    })
    return map
  }, [topScorerPredictions])

  useEffect(() => {
    if (!isOpen || !roomId || !playerId || (viewType !== 'games' && viewType !== 'upcoming') || !effectiveGameType) return
    if (demoPlayerBundle) {
      setFutureChampionPredictions(demoPlayerBundle.futureChampionPredictions ?? [])
      setTopScorerPredictions(demoPlayerBundle.topScorerPredictions ?? [])
      setLoadingExtras(false)
      return
    }
    let cancelled = false
    setLoadingExtras(true)
    Promise.all([
      futureChampionService.getPlayerPredictions(roomId, playerId),
      effectiveGameType === 'football'
        ? topScorerService.getPlayerPredictions(roomId, playerId)
        : Promise.resolve([]),
    ])
      .then(([fcList, tsList]) => {
        if (cancelled) return
        setFutureChampionPredictions(Array.isArray(fcList) ? fcList : [])
        setTopScorerPredictions(Array.isArray(tsList) ? tsList : [])
      })
      .catch(() => {
        if (!cancelled) {
          setFutureChampionPredictions([])
          setTopScorerPredictions([])
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingExtras(false)
      })
    return () => { cancelled = true }
  }, [isOpen, roomId, playerId, effectiveGameType, viewType, demoPlayerBundle])

  useEffect(() => {
    if (!isOpen) {
      setPlayerStats(null)
      setStatsPeriod('30d')
      return
    }
    if (viewType !== 'stats' || !roomId || !playerId) return

    if (demoPlayerBundle) {
      setPlayerStats(demoPlayerBundle.stats ?? null)
      setLoadingStats(false)
      return
    }

    let cancelled = false
    setLoadingStats(true)
    matchService
      .getPlayerPredictionStats(roomId, playerId, statsPeriod)
      .then((data) => {
        if (!cancelled) setPlayerStats(data as PlayerPredictionStats)
      })
      .catch(() => {
        if (!cancelled) setPlayerStats(null)
      })
      .finally(() => {
        if (!cancelled) setLoadingStats(false)
      })

    return () => { cancelled = true }
  }, [isOpen, viewType, roomId, playerId, statsPeriod, demoPlayerBundle])

  const listIsLoading = isLoading && viewType !== 'stats'
  const statsIsLoading = viewType === 'stats' && loadingStats
  const showLoading = listIsLoading || statsIsLoading

  const normalizePredictionRow = (prediction: any): DetailedPrediction | null => {
    const pid = prediction._id ?? prediction.id
    const match = prediction.match
    if (!match || !pid) return null
    const predictedScore = prediction.predictedScore
    if (predictedScore == null || String(predictedScore).trim() === '') return null
    return {
      _id: String(pid),
      winner: prediction.winner ?? null,
      predictedScore: String(predictedScore),
      match: {
        ...match,
        _id: String(match._id ?? match.id ?? ''),
      },
      predictedWinnerPoint: prediction.predictedWinnerPoint ?? null,
      predictedScorePoint: prediction.predictedScorePoint ?? null,
      predictedWinnerAdditionalTime: prediction.predictedWinnerAdditionalTime ?? null,
      predictedWinnerPenalties: prediction.predictedWinnerPenalties ?? null,
      predictedWinnerAdditionalTimePoint: prediction.predictedWinnerAdditionalTimePoint ?? null,
      predictedWinnerPenaltiesPoint: prediction.predictedWinnerPenaltiesPoint ?? null,
    }
  }

  const predictions = React.useMemo<DetailedPrediction[]>(() => {
    if (listIsLoading) return []
    if (!player.predictions || !Array.isArray(player.predictions)) {
      return []
    }

    return player.predictions
      .map(normalizePredictionRow)
      .filter((p): p is DetailedPrediction => p != null)
  }, [player, listIsLoading])

  /**
   * Games = played with known score (newest first). Upcoming = not yet resulted. Stats = all, newest first.
   */
  const listPredictionsForView = useMemo(() => {
    if (viewType === 'games') {
      const filtered = predictions.filter((p) => isMatchPlayedWithScore(p.match))
      return sortPlayedByKickoffDesc(filtered, (p) => p.match)
    }
    if (viewType === 'upcoming') {
      const filtered = predictions.filter((p) => isMatchUpcomingForPlayer(p.match))
      return sortUpcomingByKickoff(filtered, (p) => p.match)
    }
    return [...predictions].sort((a, b) => {
      const dateA = new Date(getMatchDate(a.match, true)).getTime()
      const dateB = new Date(getMatchDate(b.match, true)).getTime()
      return dateB - dateA
    })
  }, [predictions, viewType])

  // Played games = predictions on matches that already have a final score (used in the header metrics).
  const playedGamesCount = useMemo(
    () => predictions.filter((p) => isMatchPlayedWithScore(p.match)).length,
    [predictions],
  )
  const totalPoints = (player as any).totalPoints ?? 0

  const earnedPossibleBadgeClass = (earned: number, possible: number, accent: StatAccent = 'amber') => {
    if (possible > 0 && earned >= possible) return 'bg-success/20 text-success border border-success/30'
    if (earned === 0) return 'bg-destructive/20 text-destructive border border-destructive/30'
    return STAT_ACCENT[accent].badgePartial
  }

  const renderStatIcon = (Icon: LucideIcon, accent: StatAccent) => (
    <div className={`p-1.5 rounded-lg shrink-0 ${STAT_ACCENT[accent].iconBg}`}>
      <Icon className={`w-4 h-4 ${STAT_ACCENT[accent].iconColor}`} aria-hidden />
    </div>
  )

  const renderSummaryMetric = (
    Icon: LucideIcon,
    accent: StatAccent,
    label: string,
    value: string | number,
  ) => (
    <div className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 ${STAT_ACCENT[accent].iconBg} border ${STAT_ACCENT[accent].cardBorder}`}>
      {renderStatIcon(Icon, accent)}
      <span className="text-foreground/85 font-medium text-xs sm:text-small">
        {label}{' '}
        <span className="font-bold text-foreground">{value}</span>
      </span>
    </div>
  )

  const renderBreakdownCard = (
    Icon: LucideIcon,
    title: string,
    bucket: PointsBreakdownBucket,
    accent: StatAccent,
  ) => (
    <div className={`glass-card rounded-lg sm:rounded-xl p-3 sm:p-4 space-y-2 sm:space-y-2.5 border-l-4 ${STAT_ACCENT[accent].border} ${STAT_ACCENT[accent].cardBorder}`}>
      <div className="flex items-center gap-2">
        {renderStatIcon(Icon, accent)}
        <span className={`font-semibold text-small sm:text-body ${STAT_ACCENT[accent].iconColor}`}>{title}</span>
      </div>
      <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-small sm:text-body">
        <span className="text-foreground/90 font-medium">
          {t('stats.correctOf', { correct: String(bucket.correct), total: String(bucket.count) })}
        </span>
        <span className={`ml-auto inline-flex items-center px-3 py-1 rounded-lg font-bold ${earnedPossibleBadgeClass(bucket.earned, bucket.possible, accent)}`}>
          {formatEarnedPossible(bucket.earned, bucket.possible, t)}
        </span>
      </div>
    </div>
  )

  const renderExtrasCard = (
    Icon: LucideIcon,
    title: string,
    earned: number,
    possible: number,
    accent: StatAccent,
  ) => (
    <div className={`glass-card rounded-lg sm:rounded-xl p-3 sm:p-4 space-y-2 sm:space-y-2.5 border-l-4 ${STAT_ACCENT[accent].border} ${STAT_ACCENT[accent].cardBorder}`}>
      <div className="flex items-center gap-2">
        {renderStatIcon(Icon, accent)}
        <span className={`font-semibold text-small sm:text-body ${STAT_ACCENT[accent].iconColor}`}>{title}</span>
      </div>
      <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-small sm:text-body">
        <span className={`ml-auto inline-flex items-center px-3 py-1 rounded-lg font-bold ${earnedPossibleBadgeClass(earned, possible, accent)}`}>
          {formatEarnedPossible(earned, possible, t)}
        </span>
      </div>
    </div>
  )

  const statsMetaLabel = 'text-foreground/85 font-medium'

  const renderTournamentExtras = () => {
    if ((viewType !== 'games' && viewType !== 'upcoming') || !effectiveGameType) return null
    return (
      <div className="mb-3 sm:mb-4 flex flex-wrap gap-2 sm:gap-3">
        {loadingExtras ? (
          <span className="text-small text-muted-foreground">{t('stats.loadingPredictions')}</span>
        ) : slotsToShow.length === 0 && topScorerSlotsToShow.length === 0 ? (
          <span className="text-small text-muted-foreground italic">{t('futureChampion.noEvents') || 'No tournament extras'}</span>
        ) : (
          <div className="flex flex-row gap-2 sm:gap-3 w-full">
            {topScorerSlotsToShow.map((event) => {
              const pred = topScorerPredictionByEventId[event.id]
              const value = pred?.predictedScorer ?? 'Upcoming'
              const hasResult = !!event.actualTopScorer
              const points = pred?.points ?? 0
              return (
                <div
                  key={`ts-${event.id}`}
                  className="flex-1 min-w-0 rounded-md bg-muted/80 text-foreground border border-border/60 px-2.5 py-1.5 sm:px-3 sm:py-2"
                >
                  <p className="text-xs sm:text-small font-medium text-muted-foreground mb-0.5">{t('topScorer.title')}</p>
                  <p className="text-small sm:text-body font-semibold text-foreground break-words">
                    <span className={!pred ? 'text-muted-foreground italic' : 'text-primary'}>{value}</span>
                    {hasResult && points > 0 && pred && (
                      <span className="text-success text-xs sm:text-small ml-1">+{formatPoints(points, t)}</span>
                    )}
                  </p>
                </div>
              )
            })}
            {slotsToShow.map((event) => {
              const pred = predictionByEventId[event.id ?? (event as any)._id]
              const rawValue = pred ? pred.predictedChampion : null
              const value = rawValue ? String(rawValue).replace(/^[\s"{]+|[\s"}]+$/g, '').trim() : 'Upcoming'
              const hasResult = !!event.actualChampion
              const points = pred?.points ?? 0
              return (
                <div
                  key={event.id ?? (event as any)._id}
                  className="flex-1 min-w-0 rounded-md bg-muted/80 text-foreground border border-border/60 px-2.5 py-1.5 sm:px-3 sm:py-2"
                >
                  <p className="text-xs sm:text-small font-medium text-muted-foreground mb-0.5">{t('futureChampion.championShort')}</p>
                  <p className="text-small sm:text-body font-semibold text-foreground break-words">
                    <span className={value === 'Upcoming' ? 'text-muted-foreground italic' : 'text-primary'}>{value}</span>
                    {hasResult && points > 0 && pred && (
                      <span className="text-success text-xs sm:text-small ml-1">+{formatPoints(points, t)}</span>
                    )}
                  </p>
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  const pointsBadgeClass = (value: number) =>
    value === 0
      ? 'bg-destructive/20 text-destructive'
      : 'bg-success/20 text-success'

  // Removed the local formatDate function - using the imported one from dateHelper

  if (!isOpen) return null

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Close modal when clicking on the backdrop (outside the modal content)
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="w-full max-w-2xl mx-2 sm:mx-4 glass-strong rounded-xl sm:rounded-2xl p-3 sm:p-5 lg:p-6 h-[80dvh] max-h-[80dvh] sm:h-[80vh] sm:max-h-[80vh] overflow-hidden flex flex-col min-h-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start gap-2 mb-2 sm:mb-6 shrink-0">
          <div className="flex items-start gap-2 sm:gap-4 min-w-0 flex-1">
            <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-primary flex items-center justify-center shrink-0">
              <span className="text-primary-foreground font-bold text-lg sm:text-display-2">
                {player.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-lg sm:text-display-2 font-display font-bold text-foreground truncate">{player.name}</h2>
              {player.roomRole && <p className="text-small sm:text-body-lg text-muted-foreground truncate">{player.roomRole}</p>}
              <div className="flex flex-wrap items-center gap-2 mt-1.5">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs sm:text-small font-bold bg-primary/15 text-primary border border-primary/30">
                  <Trophy className="w-3.5 h-3.5 shrink-0" aria-hidden />
                  {formatPoints(totalPoints, t)}
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs sm:text-small font-medium bg-sky-500/15 text-sky-300 border border-sky-500/25">
                  <Gamepad2 className="w-3.5 h-3.5 shrink-0" aria-hidden />
                  {playedGamesCount} {t('roomPage.played')}
                </span>
              </div>
              {viewType === 'stats' && !statsIsLoading && (
                <div className="mt-2 space-y-2">
                  <div className="flex flex-wrap gap-2">
                    {(['30d', 'all'] as StatsPeriod[]).map((period) => {
                      const active = statsPeriod === period
                      return (
                        <button
                          key={period}
                          type="button"
                          onClick={() => setStatsPeriod(period)}
                          className={`px-3 py-1.5 rounded-lg text-xs sm:text-small font-semibold border transition-colors ${
                            active
                              ? 'bg-primary/15 border-primary/50 text-primary'
                              : 'bg-muted/50 border-border/60 text-foreground/75 hover:text-foreground'
                          }`}
                        >
                          {period === '30d' ? t('stats.period30d') : t('stats.periodAll')}
                        </button>
                      )
                    })}
                  </div>
                  {playerStats && (
                    <>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-bold text-xs sm:text-small ${earnedPossibleBadgeClass(playerStats.summary.earnedPoints, playerStats.summary.possiblePoints, 'sky')}`}>
                          <BarChart3 className="w-3.5 h-3.5 shrink-0" aria-hidden />
                          {formatEarnedPossible(playerStats.summary.earnedPoints, playerStats.summary.possiblePoints, t)}
                        </span>
                        <span className={`inline-flex items-center gap-1.5 text-xs sm:text-small ${statsMetaLabel}`}>
                          <CheckCircle2 className="w-3.5 h-3.5 text-violet-400 shrink-0" aria-hidden />
                          {t('stats.efficiency')}{' '}
                          <span className="font-semibold text-foreground">{playerStats.summary.efficiencyPct}%</span>
                        </span>
                      </div>
                      <p className={`inline-flex items-center gap-1.5 text-xs sm:text-small ${statsMetaLabel}`}>
                        <Trophy className="w-3.5 h-3.5 text-amber-400 shrink-0" aria-hidden />
                        {t('stats.rankingTotal')}:{' '}
                        <span className="font-semibold text-foreground">
                          {formatPoints(playerStats.rankingTotalPoints, t)}
                        </span>
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-all shrink-0" aria-label="Close">
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Tabs: Played / Upcoming / Stats — toggle the view inside this same modal */}
        <div className="flex gap-2 mb-3 sm:mb-4 shrink-0">
          {([
            { key: 'games', label: t('roomPage.played'), Icon: Gamepad2 },
            { key: 'upcoming', label: t('roomPage.upcoming'), Icon: Clock },
            { key: 'stats', label: t('roomPage.stats'), Icon: BarChart3 },
          ] as const).map(({ key, label, Icon }) => {
            const active = viewType === key
            return (
              <button
                key={key}
                type="button"
                onClick={() => setViewType(key)}
                className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs sm:text-small font-semibold border transition-colors ${
                  active
                    ? 'bg-primary/15 border-primary/50 text-primary'
                    : 'bg-muted/50 border-border/60 text-foreground/75 hover:text-foreground'
                }`}
              >
                <Icon className="w-3.5 h-3.5 shrink-0" aria-hidden />
                {label}
              </button>
            )
          })}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain -mx-1 px-1">
          {viewType === 'games' || viewType === 'upcoming' ? (
            <>
              {viewType === 'games' && (startingEntries.length > 0 || startingTotal !== 0) && (
                <div className="mb-3 sm:mb-4">
                  <div className={`glass-card rounded-lg sm:rounded-xl p-3 sm:p-4 border-l-4 ${STAT_ACCENT.amber.border} ${STAT_ACCENT.amber.cardBorder}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        {renderStatIcon(Coins, 'amber')}
                        <span className={`font-semibold text-small sm:text-body ${STAT_ACCENT.amber.iconColor}`}>{t('roomPage.adminPoints')}</span>
                      </div>
                      <span className={`inline-flex items-center px-3 py-1 rounded-lg font-bold ${STAT_ACCENT.amber.badgePartial}`}>
                        {startingTotal >= 0 ? '+' : ''}{formatPoints(startingTotal, t)}
                      </span>
                    </div>
                    {startingEntries.length > 0 && (
                      <ul className="mt-3 space-y-2 border-t border-border/40 pt-3">
                        {startingEntries.map((entry) => (
                          <li key={entry.id} className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-xs text-muted-foreground">
                                {new Date(entry.createdAt).toLocaleString()}
                                {entry.setByName ? ` · ${entry.setByName}` : ''}
                              </div>
                              {entry.note && (
                                <div className="text-small text-foreground/90 truncate">{entry.note}</div>
                              )}
                            </div>
                            <span
                              className={`shrink-0 inline-flex items-center px-2.5 py-0.5 rounded-lg text-small font-bold border ${
                                entry.amount >= 0
                                  ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                                  : 'bg-red-500/15 text-red-300 border-red-500/30'
                              }`}
                            >
                              {entry.amount >= 0 ? '+' : '−'}{formatPoints(Math.abs(entry.amount), t)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
              {renderTournamentExtras()}
              {showLoading ? (
                <div className="text-center py-6 sm:py-12">
                  <div className="text-4xl sm:text-5xl lg:text-6xl mb-3 sm:mb-4 animate-spin inline-block" aria-hidden>⚽</div>
                  <h3 className="text-lg sm:text-display-2 font-semibold text-foreground mb-1.5 sm:mb-2">{t('stats.loadingPredictions')}</h3>
                  <p className="text-small sm:text-body-lg text-muted-foreground">{t('stats.loadingPredictionsDesc')}</p>
                </div>
              ) : !listPredictionsForView.length && !listIsLoading ? (
                <div className="text-center py-6 sm:py-12">
                  <div className="text-4xl sm:text-6xl mb-3 sm:mb-4">{viewType === 'upcoming' ? '📅' : '🎮'}</div>
                  <h3 className="text-base sm:text-lg font-semibold text-foreground mb-1.5 sm:mb-2">
                    {viewType === 'upcoming' ? t('predictions.noUpcomingGames') : t('stats.noPlayedGamesWithScore')}
                  </h3>
                  {viewType === 'games' && (
                    <p className="text-small sm:text-body text-muted-foreground">{t('stats.noPlayedGamesWithScoreDesc')}</p>
                  )}
                </div>
              ) : (
                <>
                  <div className="space-y-3 sm:space-y-4">
                    {listPredictionsForView.map(prediction => {
                      // Get the full match date by combining date and time
                      const fullMatchDate = getMatchDate(prediction.match, true)
                      const matchTime = new Date(fullMatchDate).getTime()
                      const matchNotStarted = !Number.isNaN(matchTime) && matchTime > Date.now()
                      const currentUserIdNorm = String((currentUser as any)?._id ?? (currentUser as any)?.id ?? '')
                      const predictionOwnerIdNorm = String(player._id ?? (player as any)?.id ?? '')
                      const isViewingOwn = currentUserIdNorm && predictionOwnerIdNorm && currentUserIdNorm === predictionOwnerIdNorm
                      // Played: always show. Upcoming: own pick always; others see "Predicted" until kickoff.
                      const isHidden = viewType === 'games'
                        ? false
                        : !isViewingOwn && matchNotStarted

                      return (
                        <div key={prediction._id} className="glass-card rounded-lg sm:rounded-xl p-3 sm:p-4">
                          <div className="space-y-1.5 sm:space-y-2">
                            <div className="flex items-start justify-between gap-2 sm:gap-3">
                              <div className="flex items-center flex-wrap gap-2 min-w-0 flex-1">
                                <MatchTeamsInline
                                  teamA={prediction.match.teamA}
                                  teamB={prediction.match.teamB}
                                  league={resolveMatchLeague(prediction.match.league, singleRoom?.leagues)}
                                  separator="-"
                                  nameClassName="font-semibold text-small sm:text-body text-foreground"
                                />
                                {viewType === 'games' && prediction.match.score != null && String(prediction.match.score).trim() !== '' && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-lg bg-primary/20 text-primary font-semibold text-xs sm:text-small border border-primary/30 shrink-0">
                                    {t('roomPage.realScore')}: {prediction.match.score}
                                  </span>
                                )}
                              </div>
                              <span className="text-small sm:text-body text-muted-foreground shrink-0 text-right whitespace-nowrap pt-0.5">
                                {formatDate(getMatchDate(prediction.match), true)}
                              </span>
                            </div>
                            <div className="text-primary font-medium text-small sm:text-body">
                              {!isHidden && prediction.predictedScore && (() => {
                                const predictedWinner = prediction.winner
                                const winnerStr = predictedWinner ? String(predictedWinner).trim() : null
                                const teamAStr = String(prediction.match.teamA).trim()
                                const teamBStr = String(prediction.match.teamB).trim()

                                let winnerText = ''
                                if (!predictedWinner || predictedWinner === null || predictedWinner === '' || predictedWinner === 'null') {
                                  winnerText = ` (${t('stats.draw')})`
                                } else if (winnerStr === teamAStr) {
                                  winnerText = ` (${t('stats.teamWins', { team: prediction.match.teamA })})`
                                } else if (winnerStr === teamBStr) {
                                  winnerText = ` (${t('stats.teamWins', { team: prediction.match.teamB })})`
                                } else {
                                  winnerText = ` (${t('stats.teamWins', { team: String(predictedWinner) })})`
                                }

                                // Show stored score as-is (teamA-teamB); never reverse for display
                                return `${t('stats.playerScoreLabel')} ${formatPredictionScore(prediction.predictedScore, isHidden, t)}${winnerText}`
                              })()}
                              {isHidden && `${t('stats.playerScoreLabel')} ${formatPredictionScore(prediction.predictedScore, isHidden, t)}`}
                            </div>
                            {viewType === 'upcoming' && !isHidden && (() => {
                              const playoff = (prediction.match as any)?.playoff === true
                              if (!playoff) return null
                              const etPredStr = (prediction as any).predictedWinnerAdditionalTime != null
                                ? String((prediction as any).predictedWinnerAdditionalTime).trim()
                                : ''
                              const penPredStr = (prediction as any).predictedWinnerPenalties != null
                                ? String((prediction as any).predictedWinnerPenalties).trim()
                                : ''
                              if (!etPredStr && !penPredStr) return null
                              return (
                                <>
                                  {etPredStr ? (
                                    <div className="text-small sm:text-body text-muted-foreground">
                                      {t('stats.winnerExtraTime')}:{' '}
                                      <span className="text-primary font-medium">{etPredStr}</span>
                                    </div>
                                  ) : null}
                                  {penPredStr ? (
                                    <div className="text-small sm:text-body text-muted-foreground">
                                      {t('stats.winnerPenalties')}:{' '}
                                      <span className="text-primary font-medium">{penPredStr}</span>
                                    </div>
                                  ) : null}
                                </>
                              )
                            })()}
                            {viewType !== 'upcoming' && (() => {
                              const winnerPts = isHidden ? 0 : Number(prediction.predictedWinnerPoint) || 0
                              const scorePts = isHidden ? 0 : Number(prediction.predictedScorePoint) || 0
                              const etPts = isHidden ? 0 : Number((prediction as any).predictedWinnerAdditionalTimePoint) || 0
                              const penPts = isHidden ? 0 : Number((prediction as any).predictedWinnerPenaltiesPoint) || 0
                              const totalPoints = winnerPts + scorePts + etPts + penPts
                              const playoff = (prediction.match as any)?.playoff === true
                              const hasActualET = prediction.match.winnerAdditionalTime != null && String(prediction.match.winnerAdditionalTime).trim() !== ''
                              const hasPredictedET = (prediction as any).predictedWinnerAdditionalTime != null && String((prediction as any).predictedWinnerAdditionalTime).trim() !== ''
                              const showEtRow = playoff && (hasActualET || (hasPredictedET && (isViewingOwn || !matchNotStarted)))
                              const etActualStr = prediction.match.winnerAdditionalTime != null ? String(prediction.match.winnerAdditionalTime).trim() : ''
                              const etPredStr = (prediction as any).predictedWinnerAdditionalTime != null ? String((prediction as any).predictedWinnerAdditionalTime).trim() : ''
                              const etDisplayName = etActualStr || (!isHidden && etPredStr ? etPredStr : '') || '–'
                              const hasActualPen = prediction.match.winnerPenalties != null && String(prediction.match.winnerPenalties).trim() !== ''
                              const hasPredictedPen = (prediction as any).predictedWinnerPenalties != null && String((prediction as any).predictedWinnerPenalties).trim() !== ''
                              const showPenRow = playoff && (hasActualPen || (hasPredictedPen && (isViewingOwn || !matchNotStarted)))
                              const penActualStr = prediction.match.winnerPenalties != null ? String(prediction.match.winnerPenalties).trim() : ''
                              const penPredStr = (prediction as any).predictedWinnerPenalties != null ? String((prediction as any).predictedWinnerPenalties).trim() : ''
                              const penDisplayName = penActualStr || (!isHidden && penPredStr ? penPredStr : '') || '–'

                              return (
                                <div className="mt-2 sm:mt-3 flex flex-wrap items-center gap-3 sm:gap-4 text-small sm:text-body">
                                  <span className="text-muted-foreground">
                                    {t('stats.resultLabel')}:{' '}
                                    <span className="font-semibold text-foreground">{winnerPts}</span>
                                  </span>
                                  <span className="text-muted-foreground">
                                    {t('stats.scoreLabel')}:{' '}
                                    <span className="font-semibold text-foreground">{scorePts}</span>
                                  </span>
                                  {showEtRow && (
                                    <span className="text-muted-foreground">
                                      {t('stats.winnerExtraTime')} ({etDisplayName}):{' '}
                                      <span className="font-semibold text-foreground">{etPts}</span>
                                    </span>
                                  )}
                                  {showPenRow && (
                                    <span className="text-muted-foreground">
                                      {t('stats.winnerPenalties')} ({penDisplayName}):{' '}
                                      <span className="font-semibold text-foreground">{penPts}</span>
                                    </span>
                                  )}
                                  <span
                                    className={`ml-auto inline-flex items-center px-3 py-1 rounded-lg font-bold ${
                                      isHidden || totalPoints === 0
                                        ? 'bg-destructive/20 text-destructive'
                                        : 'bg-success/20 text-success'
                                    }`}
                                  >
                                    {formatPoints(isHidden ? 0 : totalPoints, t)}
                                  </span>
                                </div>
                              )
                            })()}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  {hasMorePredictions && (
                    <div className="mt-3 sm:mt-4 flex justify-center">
                      <button
                        type="button"
                        onClick={onLoadMore}
                        disabled={isLoadingMore}
                        className="px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-foreground text-small sm:text-body font-medium disabled:opacity-60 transition-opacity"
                      >
                        {isLoadingMore ? t('game.loading') : t('stats.loadMore')}
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          ) : statsIsLoading ? (
                <div className="text-center py-6 sm:py-12">
                  <div className="text-4xl sm:text-5xl lg:text-6xl mb-3 sm:mb-4 animate-spin inline-block" aria-hidden>⚽</div>
                  <h3 className="text-lg sm:text-display-2 font-semibold text-foreground mb-1.5 sm:mb-2">{t('stats.loadingStats')}</h3>
                  <p className="text-small sm:text-body-lg text-muted-foreground">{t('stats.loadingStatsDesc')}</p>
                </div>
              ) : !playerStats ? (
                <div className="glass-card rounded-lg sm:rounded-xl p-3 sm:p-4 text-center text-small sm:text-body text-foreground/80 font-medium">
                  {t('stats.noPlayedInPeriod')}
                </div>
              ) : (
                <div className="space-y-3 sm:space-y-4">
                  <div className={`glass-card rounded-lg sm:rounded-xl p-3 sm:p-4 border-l-4 ${STAT_ACCENT.sky.border} ${STAT_ACCENT.sky.cardBorder}`}>
                    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
                      <div className="flex flex-wrap gap-2 sm:gap-3">
                        {renderSummaryMetric(
                          Gamepad2,
                          'sky',
                          t('stats.totalGames').replace(/:$/, ''),
                          playerStats.summary.playedWithScore,
                        )}
                        {renderSummaryMetric(
                          CheckCircle2,
                          'violet',
                          t('stats.resultAccuracy').replace(/:$/, ''),
                          `${playerStats.summary.resultAccuracyPct}%`,
                        )}
                        {renderSummaryMetric(
                          Target,
                          'cyan',
                          t('stats.scoreAccuracy').replace(/:$/, ''),
                          `${playerStats.summary.scoreAccuracyPct}%`,
                        )}
                      </div>
                      <span className={`self-start sm:ml-auto inline-flex items-center gap-1.5 px-3 py-1 rounded-lg font-bold ${earnedPossibleBadgeClass(playerStats.summary.earnedPoints, playerStats.summary.possiblePoints, 'sky')}`}>
                        <BarChart3 className="w-4 h-4 shrink-0" aria-hidden />
                        {formatEarnedPossible(playerStats.summary.earnedPoints, playerStats.summary.possiblePoints, t)}
                      </span>
                    </div>
                  </div>

                  {renderBreakdownCard(Trophy, t('stats.winners'), playerStats.breakdown.winners, 'amber')}
                  {renderBreakdownCard(Scale, t('stats.draws'), playerStats.breakdown.draws, 'slate')}
                  {renderBreakdownCard(Target, t('stats.scores'), playerStats.breakdown.scores, 'cyan')}

                  {playerStats.breakdown.extraTime.count > 0 && renderBreakdownCard(Clock, t('stats.extraTime'), playerStats.breakdown.extraTime, 'violet')}
                  {playerStats.breakdown.penalties.count > 0 && renderBreakdownCard(Flag, t('stats.penaltiesShort'), playerStats.breakdown.penalties, 'orange')}

                  {playerStats.extras.futureChampion.events > 0 && renderExtrasCard(
                    Medal,
                    t('stats.futureChampionExtras'),
                    playerStats.extras.futureChampion.earned,
                    playerStats.extras.futureChampion.possible,
                    'blue',
                  )}
                  {playerStats.extras.topScorer.events > 0 && renderExtrasCard(
                    Target,
                    t('stats.topScorerExtras'),
                    playerStats.extras.topScorer.earned,
                    playerStats.extras.topScorer.possible,
                    'emerald',
                  )}

                  {playerStats.summary.playedWithScore === 0
                    && playerStats.extras.futureChampion.events === 0
                    && playerStats.extras.topScorer.events === 0 && (
                    <div className="glass-card rounded-lg sm:rounded-xl p-3 sm:p-4 text-center text-small sm:text-body text-foreground/80 font-medium">
                      {t('stats.noPlayedInPeriod')}
                    </div>
                  )}
                </div>
              )}
        </div>

        <div className="mt-2 pt-2 sm:mt-6 sm:pt-6 border-t border-muted/30 shrink-0">
          <button
            onClick={onClose}
            className="w-full px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl text-small sm:text-nav font-semibold bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
          >
            {t('stats.close')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ViewPlayerStatsGamesModal
